import React from 'react';
import { Icon, StatusPill, Avatar, Menu } from '../ui';
import { getInitials, statusToPill } from '../dashboard/projectUtils';

/**
 * Project-detail chrome bar. 56px sticky at the very top of every
 * /projects/:slug/* route. Replaces the legacy `.app-header` "Project
 * Detail" title bar.
 *
 * Layout (per Schedule handoff §1 "Project Header"):
 *   < Projects │ [PA] Project name [Active]              [↗] [🔔] │ [DB]
 *                  #CODE · Client · PM Name · Apr 30 → Oct 24, 2027
 *
 * Share + Bell are visual stubs in v1 — they hook to onShare / onNotify
 * if provided, otherwise no-op so they can be wired later without
 * touching this component.
 */
export default function ProjectHeader({
  project,
  summary,            // { earliest_task_start, latest_task_finish }
  user,
  onBack,
  onShare,
  onNotify,
  formatDate,         // injected from caller so date formatting matches
  darkMode,
  onToggleDarkMode,
  onLogout,
}) {
  const initials = getInitials(project);
  const pill = statusToPill(project.status);
  const startDate = summary?.earliest_task_start || project.start_date;
  const endDate = summary?.latest_task_finish || project.end_date;
  const hasManagers =
    project.project_manager && project.project_manager.includes(',');

  return (
    <header
      style={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        // Match TabBar and KpiStrip horizontal padding so the chrome has
        // a consistent inset on wide screens where the sidebar is hidden.
        // 32px (was 24) gives the back button + right-side controls
        // enough breathing room from the viewport edge — at 24 the
        // user-menu avatar was visually jammed against the right edge
        // on common 1440-1850px viewports.
        padding: '0 32px',
        background: 'var(--bg-surface)',
        borderTop: '1px solid var(--border-subtle)',
        borderLeft: '1px solid var(--border-subtle)',
        borderRight: '1px solid var(--border-subtle)',
        borderBottom: '1px solid var(--border-subtle)',
        // Rounded top corners so the chrome reads as a card stack on
        // --bg-page (matches the G702 / KpiCard radius below).
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Back button */}
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          height: 28,
          padding: '0 8px',
          border: 'none',
          background: 'transparent',
          color: 'var(--text-3)',
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          borderRadius: 6,
          transition: 'background .12s, color .12s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--bg-hover)';
          e.currentTarget.style.color = 'var(--text-1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--text-3)';
        }}
      >
        <Icon name="caret" size={14} style={{ transform: 'rotate(90deg)' }} />
        Projects
      </button>

      <span
        style={{
          width: 1,
          height: 18,
          background: 'var(--border-default)',
          flexShrink: 0,
        }}
      />

      {/* Project avatar (gradient) */}
      {project.logo_url ? (
        <img
          src={project.logo_url}
          alt=""
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            objectFit: 'cover',
            border: '1px solid var(--border-subtle)',
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background:
              'linear-gradient(135deg, var(--accent), oklch(0.42 0.06 145))',
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            fontSize: 13.5,
            letterSpacing: '-0.02em',
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
      )}

      {/* Title block */}
      <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: 16.5,
              fontWeight: 600,
              color: 'var(--text-1)',
              letterSpacing: '-0.015em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
            title={project.project_name}
          >
            {project.project_name}
          </span>
          <StatusPill status={pill.status} label={pill.label} />
        </div>
        <div
          className="mono"
          style={{
            fontSize: 13,
            color: 'var(--text-3)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            letterSpacing: '-0.01em',
          }}
        >
          {project.project_number && (
            <>
              <span>#{project.project_number}</span>
              <Dot />
            </>
          )}
          {project.client_name && (
            <>
              <span>{project.client_name}</span>
              <Dot />
            </>
          )}
          {project.project_manager && (
            <>
              <span>
                {hasManagers ? 'PMs ' : 'PM '}
                {project.project_manager}
              </span>
              {startDate && endDate && <Dot />}
            </>
          )}
          {startDate && endDate && formatDate && (
            <span>
              {formatDate(startDate)} → {formatDate(endDate)}
            </span>
          )}
        </div>
      </div>

      {/* Right cluster */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <ChromeIconBtn name="share" label="Share project" onClick={onShare} />
        <ChromeIconBtn name="bell" label="Project notifications" onClick={onNotify} />
        <span
          style={{
            width: 1,
            height: 18,
            background: 'var(--border-default)',
            margin: '0 6px',
            flexShrink: 0,
          }}
        />
        <Menu
          trigger={(toggle) => (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggle(e);
              }}
              title={user ? `${user.first_name} ${user.last_name}` : 'Account'}
              style={{
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                borderRadius: 8,
                display: 'inline-flex',
              }}
            >
              <Avatar
                initials={`${(user?.first_name || '?')[0]}${(user?.last_name || '')[0] || ''}`}
                size={28}
                accent
              />
            </button>
          )}
          items={[
            {
              label: darkMode ? 'Switch to light mode' : 'Switch to dark mode',
              onClick: () => onToggleDarkMode && onToggleDarkMode(),
            },
            { divider: true },
            {
              label: 'Sign out',
              danger: true,
              onClick: () => onLogout && onLogout(),
            },
          ]}
        />
      </div>
    </header>
  );
}

/* -------------------- helpers -------------------- */

function Dot() {
  return <span style={{ color: 'var(--text-4)' }}>·</span>;
}

function ChromeIconBtn({ name, label, onClick }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28,
        height: 28,
        borderRadius: 6,
        border: 'none',
        background: hover ? 'var(--bg-hover)' : 'transparent',
        color: hover ? 'var(--text-1)' : 'var(--text-3)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: 'background .12s, color .12s',
      }}
    >
      <Icon name={name} size={15} />
    </button>
  );
}
